import { getClient } from "foundation-client";

export const QUOTE_COLORS = [
  "basic",
  "funny",
  "awesome",
  "important",
  "hot",
  "cool",
  "tiny",
  "interesting",
] as const;

export type QuoteColor = (typeof QUOTE_COLORS)[number];

export type CreateQuoteInput = {
  id?: string;
  start_xpointer: string;
  end_xpointer: string;
  quote_text: string;
  note_text?: string;
  color?: QuoteColor;
};

export type UpdateQuoteInput = CreateQuoteInput & {
  id: string;
  created_at: string;
  title?: string;
  is_epub: boolean;
};

export async function getQuotes(artId: number) {
  const client = getClient();
  const res = await client.GET("/api/users/me/arts/{art_id}/fb3/quotes", {
    params: {
      path: { art_id: artId },
    },
  });
  if (res.response?.status === 403) {
    throw new Error("Forbidden");
  }
  const data = res.data?.payload?.data;
  return Array.isArray(data) ? data : [];
}

export async function postQuote(artId: number, params: UpdateQuoteInput) {
  const client = getClient();
  const res = await client.POST("/api/users/me/arts/{art_id}/fb3/quotes", {
    params: { path: { art_id: artId } },
    body: { ...params, is_epub: false },
  });
  if (res.error) throw new Error("Failed to create quote");
  return res;
}

export async function deleteQuote(artId: number, quote_id: string) {
  const client = getClient();
  const res = await client.DELETE(
    "/api/users/me/arts/{art_id}/fb3/quotes/{quote_id}",
    {
      params: {
        path: { art_id: artId, quote_id: quote_id },
      },
    }
  );
  if (res.error) throw new Error("Failed to delete quote");
  return res;
}

export async function updateQuote(artId: number, quote: UpdateQuoteInput) {
  const client = getClient();
  const res = await client.PATCH(
    "/api/users/me/arts/{art_id}/fb3/quotes/{quote_id}",
    {
      params: {
        path: { art_id: artId, quote_id: quote.id },
      },
      body: { ...quote, updated_at: new Date().toISOString() },
    }
  );
  if (res.error) throw new Error("Failed to update quote");
  return res;
}

export async function publishQuote(quoteUuid: string) {
  const client = getClient();
  const res = await client.PUT("/api/quotes/{quote_uuid}/publish", {
    params: {
      path: { quote_uuid: quoteUuid },
    },
  });
  if (res.error) throw new Error("Failed to publish quote");
  return res;
}

export type Quote = Awaited<ReturnType<typeof getQuotes>>[number];
